import type { TFunction } from 'i18next';

import { comparisonCaption, formatCount, intervalLabel, periodDelta } from '../format';
import { isPreset, type WebsitePeriod } from '../queries';
import {
  REPORT_SERIES_RGB,
  type ReportKpi,
  type ReportTable,
  type ReportTrend,
  type WebsiteReport,
} from '../report-pdf';

import { dataNotes } from './notices';
import { reportFigures } from './shape';
import type { QualityCount, QualityCounts, QualityReport } from './types';
import { countLine, footnote } from './wording';

export type QualityPdfInput = {
  report: QualityReport;
  t: TFunction;
  language: string;
  interval: WebsitePeriod;
  /** The figures in view are an earlier read's (the last numbers kept while asking again). */
  held: boolean;
  /** The active filter lines, as the screen names them. */
  filters: WebsiteReport['filters'];
  /** One page is picked: the time tile reads Time on page. */
  onPage: boolean;
};

/** The quality answer's share of the report; the page adds its logo, title, summary and tables. */
export type QualityPdfContent = Pick<
  WebsiteReport,
  | 'period'
  | 'addresses'
  | 'filters'
  | 'noFilters'
  | 'countLine'
  | 'notes'
  | 'kpis'
  | 'trend'
  | 'footnote'
  | 'footnoteTone'
> & { breakdown: ReportTable };

/** A count as the PDF prints it: `-` where the service could not give one, never 0. */
export const pdfCount = (n: QualityCount, language: string): string => (n === null ? '-' : formatCount(n, language));

const dateFmt = (language: string, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat => {
  try {
    return new Intl.DateTimeFormat(language || undefined, { ...opts, timeZone: 'UTC' });
  } catch {
    return new Intl.DateTimeFormat(undefined, { ...opts, timeZone: 'UTC' });
  }
};

const change = (current: number | null, previous: number | null): Pick<ReportKpi, 'change' | 'dir'> => {
  if (current === null || previous === null) return { change: null, dir: 'flat' };
  const d = periodDelta(current, previous);
  return { change: d.label, dir: d.dir };
};

/** Seconds in the time tile's unit: seconds under one minute, minutes with one decimal from one minute. */
const timeIn = (seconds: number, minutes: boolean, t: TFunction): string =>
  minutes ? `${Math.round((seconds / 60) * 10) / 10} ${t('min')}` : `${Math.round(seconds)} ${t('sec')}`;

export const qualityReportPdf = (input: QualityPdfInput): QualityPdfContent => {
  const { report, t, language, interval } = input;
  const { scope, meta } = report;
  const from = Date.parse(scope.current.from);
  const to = Date.parse(scope.current.to);
  const full = dateFmt(language, { dateStyle: 'medium' });
  const label = intervalLabel(interval, t, language);
  const period = isPreset(interval)
    ? t('{{period}} ({{from}} - {{to}}, UTC)', {
        period: label,
        from: full.format(from),
        to: full.format(Math.max(from, to - 1)),
      })
    : t('{{period}} (UTC)', { period: label });

  const basis = scope.filterBasis.people;
  const line = countLine(basis, scope.selectedHosts.length)
    .map((key) => t(key))
    .join(' ');

  const figures = reportFigures(report);
  const caption = comparisonCaption(interval, t);
  const seconds = figures.averageSeconds.current;
  const minutes = seconds !== null && seconds >= 60;
  const kpis: ReportKpi[] = [
    {
      label: t('People'),
      value: pdfCount(figures.people.current, language),
      ...change(figures.people.current, figures.people.previous),
      caption,
    },
    {
      label: t('Visits'),
      value: pdfCount(figures.visits.current, language),
      ...change(figures.visits.current, figures.visits.previous),
      caption,
    },
    {
      label: t('Pages viewed'),
      value: pdfCount(figures.pageViews.current, language),
      ...change(figures.pageViews.current, figures.pageViews.previous),
      caption,
    },
    {
      label: input.onPage ? t('Time on page') : t('Time on site'),
      value: seconds === null ? '-' : timeIn(seconds, minutes, t),
      ...change(seconds, figures.averageSeconds.previous),
      caption,
    },
  ];

  const points = report.current.trend;
  let trend: ReportTrend | null = null;
  if (points.length >= 2) {
    const day = dateFmt(language, { month: 'short', day: 'numeric' });
    const hour = dateFmt(language, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
    const twoDays = to - from > 24 * 60 * 60 * 1000;
    const people: number[] = [];
    for (const p of points) if (p.unflagged.people !== null) people.push(p.unflagged.people);
    const withPeople = people.length === points.length;
    trend = {
      title: withPeople ? t('People over time') : t('Pages viewed over time'),
      labels: points.map((p) => {
        const at = Date.parse(p.start);
        if (scope.timeBucket !== 'hour') return day.format(at);
        return twoDays ? `${day.format(at)} ${hour.format(at)}` : hour.format(at);
      }),
      series: [
        ...(withPeople ? [{ name: t('People'), rgb: REPORT_SERIES_RGB.visitors, points: people }] : []),
        { name: t('Pages viewed'), rgb: REPORT_SERIES_RGB.views, points: points.map((p) => p.unflagged.pageViews) },
        {
          name: t('Suspected'),
          rgb: REPORT_SERIES_RGB.suspected,
          points: points.map((p) => (withPeople ? p.suspected.people ?? 0 : p.suspected.pageViews)),
        },
        {
          name: t('Excluded'),
          rgb: REPORT_SERIES_RGB.excluded,
          points: points.map((p) => (withPeople ? p.excluded.people ?? 0 : p.excluded.pageViews)),
        },
      ],
    };
  }

  const b = report.current.breakdown;
  const off = (name: string, state: string): string =>
    state === 'not_enabled' ? `${name} (${t('not enabled')})` : name;
  const row = (name: string, c: QualityCounts): string[] => [
    name,
    formatCount(c.pageViews, language),
    formatCount(c.visits, language),
    formatCount(c.people, language),
  ];
  const breakdown: ReportTable = {
    title: t('Traffic breakdown'),
    sub: t('All selected hosts and dates, before page, source, link, campaign, and country filters'),
    columns: [
      { label: '', width: 0.4 },
      { label: t('Page views'), align: 'right' },
      { label: t('Visits'), align: 'right' },
      { label: t('People'), align: 'right' },
    ],
    rows: [
      row(t('Primary'), b.primary),
      row(t('Other / unclassified'), b.other),
      row(t('Suspected automation'), b.suspected),
      row(t('Excluded'), b.excluded),
      row(t('Confirmed tests'), b.confirmedTest),
      row(off(t('Verified bots'), meta.verifiedBots), b.verifiedBot),
      row(off(t('Staff'), meta.staff), b.staff),
      row(t('All recorded'), b.all),
    ],
    foot: [
      t('A person can have both counted and excluded visits, so people do not add up across rows.'),
      t('All recorded people = counted only + counted and excluded + excluded only'),
      t('Rules version: {{version}}', { version: meta.rulesVersion }),
    ],
    footTone: 'readable',
    empty: '',
  };

  return {
    period,
    addresses: { label: t('Addresses'), hosts: scope.selectedHosts },
    filters: input.filters,
    ...(input.filters.length === 0 ? { noFilters: t('Filters: none') } : {}),
    ...(line ? { countLine: line } : {}),
    notes: dataNotes(report, input.held).map((note) =>
      note.n === undefined ? t(note.text) : t(note.text, { n: note.n }),
    ),
    kpis,
    trend,
    breakdown,
    footnote: footnote(basis)
      .map((key) => t(key))
      .join(' '),
    footnoteTone: 'readable',
  };
};
